import { useState, useEffect } from 'react'
import { Image, Palette, Eye, EyeOff, AlertTriangle, Check, Link as LinkIcon, X, ArrowLeft } from 'lucide-react'
import { companiesApi } from '../lib/api'
import { useToast } from '../lib/ToastContext'

function generateSlug(name) {
  return (name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export default function Brand({ companyName, isPublic, onTogglePublic, companyId, onNavigate }) {
  const [logo, setLogo] = useState('')
  const [primaryColor, setPrimaryColor] = useState('#5D2A86')
  const [saved, setSaved] = useState(false)
  const [listing, setListing] = useState(isPublic ? 'public' : 'private')
  const [slug, setSlug] = useState(generateSlug(companyName))
  const [slugAvailable, setSlugAvailable] = useState(null)
  const [showVisibilityModal, setShowVisibilityModal] = useState(false)
  const [pendingListing, setPendingListing] = useState(null)
  const { toast } = useToast()

  useEffect(() => {
    setSlug(generateSlug(companyName))
  }, [companyName])

  useEffect(() => {
    if (!slug) return
    let mounted = true
    const t = setTimeout(async () => {
      const { data } = await companiesApi.getBySlug(slug)
      if (!mounted) return
      setSlugAvailable(!data)
    }, 500)
    return () => { mounted = false; clearTimeout(t) }
  }, [slug])

  const handleListingChange = (val) => {
    setPendingListing(val)
    setShowVisibilityModal(true)
  }

  const confirmVisibilityChange = async () => {
    const val = pendingListing
    setListing(val)
    setShowVisibilityModal(false)
    onTogglePublic?.(val === 'public')
    if (companyId) {
      const { error } = await companiesApi.update(companyId, {
        public_visibility: val === 'public',
        slug: val === 'public' ? slug : null,
      })
      if (error) {
        toast('Failed to update visibility: ' + error, 'error')
      } else {
        toast(val === 'public' ? 'Company is now publicly visible on Discover.' : 'Company is now private.', 'success')
      }
    } else {
      toast(val === 'public' ? 'Company is now publicly visible.' : 'Company is now private.', 'success')
    }
  }

  const save = async () => {
    if (companyId) {
      const { error } = await companiesApi.update(companyId, {
        slug: listing === 'public' ? slug : null,
      })
      if (error) {
        toast('Failed to save brand settings: ' + error, 'error')
        return
      }
    }
    setSaved(true)
    toast('Brand settings saved.', 'success')
    setTimeout(() => setSaved(false), 2200)
  }

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      <div className="flex items-end justify-between">
        <div>
          {/* {onNavigate && (
            <button onClick={() => onNavigate('dashboard')} className="btn-ghost text-xs flex items-center gap-1.5 mb-4">
              <ArrowLeft size={14} /> Back to Dashboard
            </button>
          )} */}
          <h1 className="text-2xl font-bold text-ink">Brand</h1>
          <p className="text-sm text-muted mt-1">Customize your workspace branding and directory visibility.</p>
        </div>
        {saved && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><Check size={14} /> Brand settings saved</span>}
      </div>

      {/* Logo upload */}
      <div className="surface p-7 max-w-2xl space-y-5">
        <h3 className="text-sm font-semibold text-ink flex items-center gap-2"><Image size={15} /> Company Logo</h3>
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 border border-neutral-200 bg-neutral-50 flex items-center justify-center">
            {logo ? <img src={logo} alt="Logo" className="w-full h-full object-contain" /> : <Image size={20} className="text-neutral-300" />}
          </div>
          <div className="flex-1">
            <input type="text" value={logo} onChange={(e) => setLogo(e.target.value)} className="input-flat" placeholder="Paste image URL (https://...)" />
            <p className="text-[11px] text-muted mt-1.5">PNG or SVG recommended. Max 1MB.</p>
          </div>
        </div>
      </div>

      {/* Brand colors */}
      <div className="surface p-7 max-w-2xl space-y-5">
        <h3 className="text-sm font-semibold text-ink flex items-center gap-2"><Palette size={15} /> Brand Colors</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="label-above">Primary Color</label>
            <div className="flex gap-2">
              <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-12 h-9 border border-neutral-200 cursor-pointer" />
              <input type="text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="input-flat font-mono" />
            </div>
          </div>
        </div>
      </div>

      {/* Public URL Slug */}
      <div className="surface p-7 max-w-2xl space-y-5">
        <h3 className="text-sm font-semibold text-ink flex items-center gap-2"><LinkIcon size={15} /> Public Profile URL</h3>
        <p className="text-xs text-muted">Your public profile slug determines your discoverable URL.</p>
        <div>
          <label className="label-above">Profile Slug</label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted font-mono whitespace-nowrap">bizossuite.com/company/</span>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              className="input-flat font-mono flex-1"
              placeholder="your-company-slug"
            />
          </div>
          {slug && (
            <p className={`text-[11px] mt-1.5 ${slugAvailable === false ? 'text-rose-600' : slugAvailable === true ? 'text-emerald-600' : 'text-muted'}`}>
              {slugAvailable === false ? 'This slug is already taken.' : slugAvailable === true ? 'Slug is available.' : 'Checking availability...'}
            </p>
          )}
        </div>
        <button onClick={save} className="btn-primary">Save Brand Settings</button>
      </div>

      {/* Directory listing toggle */}
      <div className="surface p-7 max-w-2xl space-y-5">
        <h3 className="text-sm font-semibold text-ink">Directory Listing</h3>
        <p className="text-xs text-muted">Control whether your company appears publicly on the Bizos Discover directory.</p>
        <div className="flex gap-3">
          <button
            onClick={() => handleListingChange('private')}
            className={`flex-1 flex items-center gap-2 px-4 py-3 text-sm font-semibold border transition-colors ${
              listing === 'private' ? 'border-brand bg-brand-soft text-brand' : 'border-neutral-200 text-neutral-500 hover:text-ink'
            }`}
          >
            <EyeOff size={15} /> Private
          </button>
          <button
            onClick={() => handleListingChange('public')}
            className={`flex-1 flex items-center gap-2 px-4 py-3 text-sm font-semibold border transition-colors ${
              listing === 'public' ? 'border-brand bg-brand-soft text-brand' : 'border-neutral-200 text-neutral-500 hover:text-ink'
            }`}
          >
            <Eye size={15} /> Public
          </button>
        </div>
        {listing === 'public' && (
          <div className="border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2.5 animate-fade-in">
            <AlertTriangle size={15} className="text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800">
              Making your company public will show your name, industry, country, and governance score to all visitors. Sensitive documents remain gated behind access requests.
            </p>
          </div>
        )}
      </div>

      {/* Visibility Confirmation Modal */}
      {showVisibilityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setShowVisibilityModal(false)} aria-hidden />
          <div className="relative bg-surface border border-neutral-200 w-full max-w-sm animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
              <h2 className="text-sm font-semibold text-ink">Confirm Visibility Change</h2>
              <button onClick={() => setShowVisibilityModal(false)} className="text-neutral-400 hover:text-ink"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-muted leading-relaxed">
                  {pendingListing === 'public'
                    ? 'Making your company public will expose your name, industry, country, and governance score to all visitors on the Bizos Discover directory. Sensitive documents remain gated behind access requests. Do you wish to proceed?'
                    : 'Making your company private will remove it from the public Discover directory. Existing investor tokens will remain active until revoked. Do you wish to proceed?'}
                </p>
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setShowVisibilityModal(false)} className="btn-secondary">Cancel</button>
                <button onClick={confirmVisibilityChange} className="btn-primary">Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
